import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  Box,
  CircularProgress,
  Container,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { ArrowCircleRight } from "@phosphor-icons/react";

import {
  CountryCode,
  LeadPastor,
  ManagerAccountRequest,
  State,
  USAddress,
  ValidationError,
} from "baptize-america-client";

import { useAlert } from "../../components/AlertProvider";
import { createChurch } from "../../utils/client";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";
import { containsUpperCase } from "../../utils/helper";

const styles = {
  titleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
  },
  headerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 2,
    mb: 2,
  },
  itemContainer: {
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "column",
    mt: 1,
    mb: 1,
    flexGrow: 1,
  },
  saveButtonBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mt: 5,
  },
  saveButton: {
    backgroundColor: "#00169E",
    color: "#FFF",
    justifyContent: "center",
    height: 50,
    width: 300,
    fontSize: 16,
  },
  stack: {
    justifyContent: "space-between",
    display: "flex",
    flex: 1,
  },
};

/**
 ** TODO: Create "Invite/Add Manager" fields + handler function
 */
const CreateChurchPage = () => {
  const [createChurchParams] = useSearchParams();
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const { setAlert } = useAlert();
  const navigate = useNavigate();
  const invitationId = createChurchParams.get("id")!;
  const [churchName, setChurchName] = useState<string>();
  const [pastorFirstName, setPastorFirstName] =
    useState<LeadPastor["firstName"]>(undefined);
  const [pastorLastName, setPastorLastName] =
    useState<LeadPastor["lastName"]>(undefined);
  const [address, setAddress] = useState<USAddress>({
    streetAddress1: "",
    streetAddress2: "",
    city: "",
    state: undefined,
    postalCode: "",
    country: CountryCode.US,
  });
  const [website, setWebsite] = useState<string | undefined>(undefined);
  const [instagramUrl, setInstagramUrl] = useState<string | undefined>(
    undefined,
  );
  const [manager, setManager] = useState<ManagerAccountRequest>({
    firstName: undefined,
    lastName: undefined,
    email: undefined,
  });
  const [confirmEmail, setConfirmEmail] = useState<string>();
  const [errors, setErrors] = useState<{
    churchName?: string;
    leadPastorFirstName?: string;
    leadPastorLastName?: string;
    managerFirstName?: string;
    managerLastName?: string;
    managerEmail?: string;
    confirmEmail?: string;
    streetAddress1?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    website?: string;
    instagram?: string;
  }>({});
  const [loading, setLoading] = useState<boolean>(false);

  const handleUpdateChurch = async () => {
    const validateInputs = () => {
      const newErrors: Record<string, string> = {};
      if (!churchName?.trim())
        newErrors.churchName = "Church name is required.";
      if (!pastorFirstName?.trim())
        newErrors.leadPastorFirstName = "First name is required.";
      if (!pastorLastName?.trim())
        newErrors.leadPastorLastName = "Last name is required.";
      if (!address.streetAddress1?.trim())
        newErrors.streetAddress1 = "Address is required.";
      if (!address.city?.trim()) newErrors.city = "City is required.";
      if (!address.state?.trim()) newErrors.state = "State is required.";
      if (!address.postalCode?.trim())
        newErrors.postalCode = "Zip Code is required.";
      if (!website?.trim()) newErrors.website = "Website is required.";
      if (website?.trim() && !website?.trim().startsWith("https://"))
        newErrors.website = "Website URL should start with https://";
      if (
        instagramUrl?.trim() &&
        !(
          instagramUrl.trim().startsWith("https://www.instagram.com/") ||
          instagramUrl.trim().startsWith("https://instagram.com/")
        )
      )
        newErrors.instagram =
          "Instagram URL should start with https://www.instagram.com/ or https://instagram.com/";
      if (!manager.firstName?.trim())
        newErrors.managerFirstName = "First name is required.";
      if (!manager.lastName?.trim())
        newErrors.managerLastName = "Last name is required.";
      if (!manager.email?.trim()) newErrors.managerEmail = "Email is required.";
      if (manager.email && containsUpperCase(manager.email))
        newErrors.managerEmail = "Email must not contain uppercase letters.";
      if (manager.email !== confirmEmail) {
        newErrors.confirmEmail = "Emails do not match.";
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    if (!invitationId) {
      setAlert({
        showAlert: true,
        alertMsg: "Missing invitationID",
        alertType: "error",
      });
      return;
    }

    if (!validateInputs()) return;

    try {
      setLoading(true);
      await createChurch({
        address: address,
        instagramUrl: instagramUrl,
        invitationId: invitationId,
        leadPastor: {
          firstName: pastorFirstName,
          lastName: pastorLastName,
        },
        manager: manager,
        name: churchName,
        websiteUrl: website,
      });
      setLoading(false);
      setAlert({
        showAlert: true,
        alertMsg: `Church created successfully. Please check the church manager's email (${manager.email}) for account information.`,
        alertType: "success",
      });
      navigate(
        `/churches/check_inbox?createChurchEmail=${encodeURIComponent(manager.email ?? "")}`,
        { replace: true },
      );
    } catch (err) {
      setLoading(false);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Creating Church",
        alertType: "error",
      });
    }
  };

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.titleBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar showBackArrow={true} />
      </Box>
      <Box sx={styles.headerBox} minWidth={theme.breakpoints.values.sm}>
        <Typography
          align={"left"}
          variant={"h5"}
          fontFamily={"AmericanGrotesk-Bold"}
        >
          {"Complete Your Church Profile"}
        </Typography>
      </Box>
      <Divider orientation="horizontal" textAlign="left">
        <Typography variant="body2">
          Create Account
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={5000}
            title="Private Information - This user can update church information, add baptism events, and manage other church data. Additional managers can be added later, and their information will remain private."
          >
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Divider>
      <Stack direction="row">
        <TextField
          required
          id="outlined-required"
          label="First Name"
          value={manager.firstName}
          error={!!errors.managerFirstName}
          helperText={errors.managerFirstName}
          onChange={(e) =>
            setManager((cur) => ({ ...cur, firstName: e.target.value }))
          }
          sx={[styles.itemContainer, { mr: 1 }]}
        />
        <TextField
          required
          id="outlined-required"
          label="Last Name"
          sx={[styles.itemContainer, { ml: 1 }]}
          value={manager.lastName}
          error={!!errors.managerLastName}
          helperText={errors.managerLastName}
          onChange={(e) =>
            setManager((cur) => ({ ...cur, lastName: e.target.value }))
          }
        />
      </Stack>
      <Stack direction="column">
        <TextField
          required
          id="outlined-required"
          label="Email"
          value={manager.email}
          error={!!errors.managerEmail}
          helperText={errors.managerEmail}
          onChange={(e) =>
            setManager((cur) => ({ ...cur, email: e.target.value }))
          }
          sx={[styles.itemContainer]}
        />
        <TextField
          required
          id="outlined-required"
          label="Confirm Email"
          value={confirmEmail}
          error={!!errors.confirmEmail}
          helperText={errors.confirmEmail}
          onChange={(e) => setConfirmEmail(e.target.value)}
          sx={[styles.itemContainer]}
        />
      </Stack>
      <Divider orientation="horizontal" textAlign="left" sx={{ mt: 1 }}>
        <Typography variant="body2">
          Church Name
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={3000}
            title="Public Information"
          >
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Divider>
      <Stack direction="column">
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Church Name"}
          value={churchName ?? ""}
          error={!!errors.churchName}
          helperText={errors.churchName}
          onChange={(e) => setChurchName(e.target.value)}
          sx={[styles.itemContainer]}
        />
      </Stack>
      <Divider orientation="horizontal" textAlign="left">
        <Typography variant="body2">
          Lead Pastor
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={3000}
            title="Public Information"
          >
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Divider>
      <Stack direction="row" sx={styles.stack}>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"First Name"}
          value={pastorFirstName ?? ""}
          error={!!errors.leadPastorFirstName}
          helperText={errors.leadPastorFirstName}
          onChange={(e) => setPastorFirstName(e.target.value)}
          sx={[styles.itemContainer, { mr: 1 }]}
        />
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Last Name"}
          value={pastorLastName ?? ""}
          error={!!errors.leadPastorLastName}
          helperText={errors.leadPastorLastName}
          onChange={(e) => setPastorLastName(e.target.value)}
          sx={[styles.itemContainer, { ml: 1 }]}
        />
      </Stack>
      <Divider orientation="horizontal" textAlign="left">
        <Typography variant="body2">
          Church Address
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={3000}
            title="Public Information - Baptism events will have its own address which may differ from the church's address."
          >
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Divider>
      <Stack direction="column" sx={styles.stack}>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Street Address"}
          error={!!errors.streetAddress1}
          helperText={errors.streetAddress1}
          value={address?.streetAddress1 ?? ""}
          onChange={(e) =>
            setAddress({ ...address, streetAddress1: e.target.value })
          }
          sx={styles.itemContainer}
        />
        <TextField
          required={false}
          id={"outlined-required"}
          label={"Unit/Suite/Other"}
          value={address?.streetAddress2 ?? ""}
          onChange={(e) =>
            setAddress({ ...address, streetAddress2: e.target.value })
          }
          sx={styles.itemContainer}
        />
      </Stack>
      <Stack direction="row" sx={styles.stack}>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"City"}
          error={!!errors.city}
          helperText={errors.city}
          value={address?.city ?? ""}
          onChange={(e) => setAddress({ ...address, city: e.target.value })}
          sx={[styles.itemContainer, { mr: 1, flex: 0.48 }]}
        />
        <TextField
          id="state-select"
          select
          required
          label="State"
          slotProps={{
            select: {
              native: true,
            },
          }}
          value={address?.state}
          error={!!errors.state}
          helperText={errors.state}
          onChange={(e) =>
            setAddress({ ...address, state: e.target.value as State })
          }
          sx={[styles.itemContainer, { mr: 1, flex: 0.24, minWidth: 75 }]}
        >
          {Object.keys(State).map((option, index) => (
            <option key={`Select_${option}_${index}`} value={option}>
              {option}
            </option>
          ))}
        </TextField>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Zip Code"}
          error={!!errors.postalCode}
          helperText={errors.postalCode}
          value={address?.postalCode ?? ""}
          onChange={(e) =>
            setAddress({ ...address, postalCode: e.target.value })
          }
          sx={[styles.itemContainer, { flex: 0.28, minWidth: 80 }]}
        />
      </Stack>
      <Divider orientation="horizontal" textAlign="left">
        <Typography variant="body2">
          Social Media
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={3000}
            title="Public Information"
          >
            <IconButton>
              <InfoIcon />
            </IconButton>
          </Tooltip>
        </Typography>
      </Divider>
      <Stack direction="column" sx={styles.stack}>
        <TextField
          required={true}
          id={"outlined-required"}
          label={"Website URL"}
          placeholder={"eg: https://baptize.org"}
          value={website ?? ""}
          error={!!errors.website}
          helperText={errors.website}
          onChange={(e) => setWebsite(e.target.value)}
          sx={styles.itemContainer}
        />
        <TextField
          id={"outlined"}
          label={"Instagram (optional)"}
          placeholder={"eg: https://instagram.com/baptizeamerica"}
          value={instagramUrl ?? ""}
          error={!!errors.instagram}
          helperText={errors.instagram}
          onChange={(e) => setInstagramUrl(e.target.value)}
          sx={styles.itemContainer}
        />
      </Stack>
      <Box sx={styles.saveButtonBox}>
        <DebouncedButton
          variant="contained"
          sx={styles.saveButton}
          disabled={loading}
          endIcon={
            loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <ArrowCircleRight size={24} weight="fill" />
            )
          }
          onClick={handleUpdateChurch}
          debounceTime={3000}
        >
          {loading ? "" : "Next Steps"}
        </DebouncedButton>
      </Box>
    </Container>
  );
};

export default CreateChurchPage;
